// The allowlist document schema. This file IS the sanitisation rule (FR-011,
// research.md D-02): it mirrors contracts/template-document.schema.json exactly,
// and any node type, mark type, or attribute not enumerated here causes the
// whole document to be rejected — never stripped and stored.
//
// Nothing representable here can carry a URL, an event handler, or raw markup,
// so there is no separate "sanitise" step to forget.

import { z } from 'zod';

/** FR-008. Leading char alphanumeric, so a name is never blank or separator-led. */
export const PLACEHOLDER_NAME_PATTERN = /^[A-Za-z0-9][A-Za-z0-9 _-]*$/;
export const PLACEHOLDER_NAME_MAX_LENGTH = 64;

export const placeholderNameSchema = z
  .string()
  .min(1)
  .max(PLACEHOLDER_NAME_MAX_LENGTH)
  .regex(PLACEHOLDER_NAME_PATTERN, 'placeholder name contains disallowed characters');

const markSchema = z.strictObject({
  type: z.enum(['bold', 'italic', 'underline']),
});

const textNodeSchema = z.strictObject({
  type: z.literal('text'),
  text: z.string().min(1),
  marks: z.array(markSchema).optional(),
});

const hardBreakNodeSchema = z.strictObject({
  type: z.literal('hardBreak'),
});

// Atomic (FR-006): no `content` key is permitted, so a placeholder cannot nest
// or hide anything inside itself.
const placeholderNodeSchema = z.strictObject({
  type: z.literal('placeholder'),
  attrs: z.strictObject({ name: placeholderNameSchema }),
});

const inlineNodeSchema = z.discriminatedUnion('type', [
  textNodeSchema,
  hardBreakNodeSchema,
  placeholderNodeSchema,
]);

const paragraphSchema = z.strictObject({
  type: z.literal('paragraph'),
  content: z.array(inlineNodeSchema).optional(),
});

const headingSchema = z.strictObject({
  type: z.literal('heading'),
  attrs: z.strictObject({ level: z.union([z.literal(1), z.literal(2), z.literal(3)]) }),
  content: z.array(inlineNodeSchema).optional(),
});

// A list item holds paragraphs only. Nested lists are not representable — FR-005
// asks for ordered and unordered lists, not nesting, and Principle V says we do
// not build for the unasked.
const listItemSchema = z.strictObject({
  type: z.literal('listItem'),
  content: z.array(paragraphSchema).min(1),
});

const bulletListSchema = z.strictObject({
  type: z.literal('bulletList'),
  content: z.array(listItemSchema).min(1),
});

const orderedListSchema = z.strictObject({
  type: z.literal('orderedList'),
  content: z.array(listItemSchema).min(1),
});

const blockNodeSchema = z.discriminatedUnion('type', [
  paragraphSchema,
  headingSchema,
  bulletListSchema,
  orderedListSchema,
]);

export const templateDocumentSchema = z.strictObject({
  type: z.literal('doc'),
  content: z.array(blockNodeSchema).min(1),
});

export type Mark = z.infer<typeof markSchema>;
export type TextNode = z.infer<typeof textNodeSchema>;
export type HardBreakNode = z.infer<typeof hardBreakNodeSchema>;
export type PlaceholderNode = z.infer<typeof placeholderNodeSchema>;
export type InlineNode = z.infer<typeof inlineNodeSchema>;
export type Paragraph = z.infer<typeof paragraphSchema>;
export type Heading = z.infer<typeof headingSchema>;
export type ListItem = z.infer<typeof listItemSchema>;
export type BlockNode = z.infer<typeof blockNodeSchema>;
export type TemplateDocument = z.infer<typeof templateDocumentSchema>;

/** One validation failure, shaped for the Error.details array in the API contract. */
export interface ValidationIssue {
  path: string;
  message: string;
}

export type ParseResult =
  { ok: true; value: TemplateDocument } | { ok: false; issues: ValidationIssue[] };

/**
 * Validate an untrusted value as a template document.
 *
 * On failure the result carries no `value` at all — there is deliberately no
 * "cleaned" document to accidentally persist.
 */
export function parseTemplateDocument(input: unknown): ParseResult {
  const result = templateDocumentSchema.safeParse(input);
  if (result.success) {
    return { ok: true, value: result.data };
  }
  return {
    ok: false,
    issues: result.error.issues.map((issue) => ({
      path: issue.path.length > 0 ? issue.path.join('.') : '(root)',
      message: issue.message,
    })),
  };
}

export function isTemplateDocument(input: unknown): input is TemplateDocument {
  return templateDocumentSchema.safeParse(input).success;
}
